import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { COMPANY_LINE, LEGAL_DOCS, LEGAL_ENTITY, SUPPORT_LINE, TRADEMARK_NOTE } from '../../data/legalDocs';

// The footer of the landing page and of every public page (legal pages, contact):
//   left   logo + name + a short description
//   centre Terms & Conditions (the whole legal pack, each document at its own URL)
//   right  Company (the legal entity) and Contact (the support e-mail)
// and a bottom line with the company identity text, the trademark note and the copyright ("Fixes Before Applications", fixes 2, 4, 5).
const C = {
  bg: '#f7f2ea', surface: '#ffffff', text: '#14161c', textSub: '#6b6459', textMuted: '#a19a8c',
  border: '#e6dfd0', borderSoft: '#efe7d8', orangeFrom: '#fbbf24', orangeTo: '#e4870e',
};
const sectionPx = { xs: 3, md: 6 };
const sectionMaxWidth = { lg: 1400, xl: 1600 };

const head = { fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: C.orangeTo, mb: 1.4 };
const linkSx = { display: 'block', whiteSpace: { sm: 'nowrap' }, fontSize: '0.86rem', fontWeight: 600, color: C.text, textDecoration: 'none', py: 0.45, '&:hover': { color: C.orangeTo, textDecoration: 'underline' } };

export default function LandingFooter() {
  return (
    <Box component="footer" sx={{ background: C.bg, borderTop: `1px solid ${C.borderSoft}`, px: sectionPx, pt: { xs: 4, md: 6 }, pb: { xs: 3, md: 3.5 } }}>
      <Box sx={{ maxWidth: sectionMaxWidth, mx: 'auto', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.7fr 1fr' }, gap: { xs: 4, md: 5 }, alignItems: 'start' }}>

        {/* left: logo, name, description */}
        <Box>
          <Box component={Link} to="/" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.2, textDecoration: 'none', mb: 1.6 }}>
            <Box component="img" src="/assests/website-logo.png" alt="MarketSync" sx={{ width: 36, height: 'auto', flexShrink: 0 }} />
            <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'stretch' }}>
              <Typography sx={{ fontSize: '1.55rem', fontWeight: 800, letterSpacing: '-0.022em', lineHeight: 1.15, whiteSpace: 'nowrap' }}>
                <Box component="span" sx={{ color: C.text }}>Market</Box>
                <Box component="span" sx={{ background: `linear-gradient(135deg, ${C.orangeFrom}, ${C.orangeTo})`, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Sync</Box>
              </Typography>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: C.textSub, letterSpacing: '0.14em', lineHeight: 1, display: 'flex', justifyContent: 'space-between', width: '100%', whiteSpace: 'nowrap' }}>
                <Box component="span">Connect</Box><Box component="span" sx={{ color: C.orangeTo }}>•</Box><Box component="span">Manage</Box><Box component="span" sx={{ color: C.orangeTo }}>•</Box><Box component="span">Grow</Box>
              </Typography>
            </Box>
          </Box>
          <Typography sx={{ fontSize: '0.88rem', color: C.textSub, lineHeight: 1.75, maxWidth: 360 }}>
            MarketSync helps online sellers manage their stores, products and orders from one place. Connect your WordPress and WooCommerce stores today, with more marketplaces on the way.
          </Typography>
        </Box>

        {/* centre: Terms & Conditions */}
        <Box>
          <Typography sx={{ ...head, textAlign: 'center', fontSize: '0.85rem', pb: 1.2, mb: 1.6, borderBottom: `1px dashed ${C.textMuted}` }}>Terms &amp; Conditions</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'auto auto' }, gridAutoFlow: 'row', columnGap: 6, justifyContent: 'center' }}>
            {LEGAL_DOCS.map((d) => <Box key={d.slug} component={Link} to={`/${d.slug}`} sx={linkSx}>{d.title}</Box>)}
          </Box>
        </Box>

        {/* right: company + contact */}
        <Box>
          <Typography sx={head}>Company</Typography>
          <Typography sx={{ fontSize: '0.86rem', color: C.textSub, lineHeight: 1.7, mb: 2 }}>
            <b style={{ color: C.text }}>{LEGAL_ENTITY.name}</b> <i>(d/b/a {LEGAL_ENTITY.dba})</i><br />
            A Wyoming limited liability company<br />
            {LEGAL_ENTITY.address}
          </Typography>
          <Typography sx={head}>Contact</Typography>
          <Typography sx={{ fontSize: '0.86rem', color: C.textSub, lineHeight: 1.7 }}>
            <Box component="a" href={`mailto:${LEGAL_ENTITY.email}`} sx={{ color: C.text, fontWeight: 700, textDecoration: 'none', '&:hover': { color: C.orangeTo } }}>{LEGAL_ENTITY.email}</Box><br />
            <Box component={Link} to="/contact" sx={{ color: C.orangeTo, fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Contact us →</Box>
          </Typography>
        </Box>
      </Box>

      <Box sx={{ maxWidth: sectionMaxWidth, mx: 'auto', mt: { xs: 3.5, md: 4.5 }, pt: 2.2, borderTop: `1px solid ${C.border}` }}>
        <Typography sx={{ fontSize: '0.74rem', color: C.textSub, lineHeight: 1.7, mb: 0.8 }}>{COMPANY_LINE}</Typography>
        <Typography sx={{ fontSize: '0.74rem', color: C.textSub, lineHeight: 1.7, mb: 0.8 }}>{SUPPORT_LINE}</Typography>
        <Typography sx={{ fontSize: '0.72rem', color: C.textMuted, lineHeight: 1.7, mb: 0.8 }}>{TRADEMARK_NOTE}</Typography>
        <Typography sx={{ fontSize: '0.72rem', color: C.textMuted }}>© {new Date().getFullYear()} {LEGAL_ENTITY.name} (d/b/a {LEGAL_ENTITY.dba}). All rights reserved.</Typography>
      </Box>
    </Box>
  );
}
